import React, { useCallback, useEffect, useState } from 'react';
import {
  BarChart3, Code2, Copy, Edit3, Eye, EyeOff, Image as ImgIcon, Loader2,
  Megaphone, MousePointerClick, Plus, Trash2, Upload, X,
} from 'lucide-react';
import client, { resolveApiAssetUrl } from '../lib/api';
import { UPLOAD_MAX_LABEL, uploadTooLarge } from '../lib/mediaAsset';
import { useToast } from '../hooks/use-toast';

/**
 * Reklam Kodları — yayıncı sitelere verilen banner kodu.
 *
 * Amaç kendi sitemizin reklamını başka sitelerde yayınlamaktır. Yayıncıya
 * iki parça kod verilir (head'e yükleyici, body'ye slot); banner sunucudan
 * geldiği için görsel, metin ve hedef panelden değişince karşı sitedeki kod
 * güncellenmez.
 *
 * Kod istemcide birleştirilmez — taban adres sunucudan gelir, çünkü panel
 * ayrı bir alan adında çalışıyor olabilir.
 */
const emptyForm = {
  name: '',
  slug: '',
  size: 'leaderboard',
  template: 'auto',
  image_url: '',
  background_image_url: '',
  headline: '',
  subtext: '',
  cta_text: 'Hemen Katıl',
  accent_color: '#e0b64a',
  accent_color_2: '',
  target_url: '',
  order: 0,
  is_active: true,
};

const AdBanners = () => {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ sizes: [], templates: [], site_base: '' });
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState('');
  // Kod penceresi: hangi banner, hangi ölçü, hangi yayıncı etiketi.
  const [codeFor, setCodeFor] = useState(null);
  const [codeSize, setCodeSize] = useState('leaderboard');
  const [publisher, setPublisher] = useState('');
  const [code, setCode] = useState(null);
  const [codeLoading, setCodeLoading] = useState(false);
  // Yayıncı kırılımı penceresi.
  const [statsFor, setStatsFor] = useState(null);
  const [stats, setStats] = useState(null);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get('/admin/ads');
      setItems(data.items || []);
      setMeta({
        sizes: data.sizes || [],
        templates: data.templates || [],
        site_base: data.site_base || '',
      });
    } catch (error) {
      toast({
        title: 'Reklamlar yüklenemedi',
        description: error.response?.data?.detail || 'İstek başarısız.',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const sizeLabel = (key) => {
    const found = meta.sizes.find((size) => size.key === key);
    return found ? `${found.label} · ${found.width}×${found.height}` : key;
  };

  const openNew = () => { setForm(emptyForm); setModal({ mode: 'new' }); };
  const openEdit = (item) => {
    setForm({ ...emptyForm, ...item });
    setModal({ mode: 'edit', id: item.id });
  };

  const uploadImage = async (event, field) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (uploadTooLarge(file)) {
      toast({ title: 'Görsel yüklenemedi', description: `Dosya en fazla ${UPLOAD_MAX_LABEL} olabilir.` });
      return;
    }
    setUploading(field);
    try {
      const body = new FormData();
      body.append('file', file);
      const { data } = await client.post('/admin/uploads/images', body);
      setForm((current) => ({ ...current, [field]: data.url }));
      toast({ title: 'Görsel yüklendi' });
    } catch (error) {
      toast({
        title: 'Görsel yüklenemedi',
        description: error.response?.data?.detail || 'Dosya yükleme başarısız.',
      });
    } finally {
      setUploading('');
    }
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Ad zorunlu', description: 'Reklama bir ad verin (yalnızca panelde görünür).' });
      return;
    }
    setBusy(true);
    try {
      if (modal.mode === 'new') await client.post('/admin/ads', form);
      else await client.patch(`/admin/ads/${modal.id}`, form);
      toast({ title: 'Kaydedildi' });
      setModal(null);
      load();
    } catch (error) {
      toast({
        title: 'Kaydedilemedi',
        description: error.response?.data?.detail || 'İstek başarısız.',
      });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(
      `"${item.name}" reklamı silinsin mi? Yayıncı sitelerdeki kod boş kalır.`,
    )) return;
    try {
      await client.delete(`/admin/ads/${item.id}`);
      toast({ title: 'Silindi' });
      load();
    } catch (error) {
      toast({ title: 'Silinemedi', description: error.response?.data?.detail || 'İstek başarısız.' });
    }
  };

  const toggle = async (item) => {
    try {
      await client.patch(`/admin/ads/${item.id}`, { is_active: !item.is_active });
      load();
    } catch (error) {
      toast({ title: 'Güncellenemedi', description: error.response?.data?.detail || 'İstek başarısız.' });
    }
  };

  const loadCode = useCallback(async (item, size, source) => {
    setCodeLoading(true);
    try {
      const { data } = await client.get(`/admin/ads/${item.id}/code`, {
        params: { size, source, template: item.template || '' },
      });
      setCode(data);
    } catch (error) {
      setCode(null);
      toast({
        title: 'Kod alınamadı',
        description: error.response?.data?.detail || 'İstek başarısız.',
      });
    } finally {
      setCodeLoading(false);
    }
  }, [toast]);

  const openCode = (item) => {
    setCodeFor(item);
    setCodeSize(item.size || 'leaderboard');
    setPublisher('');
    setCode(null);
    loadCode(item, item.size || 'leaderboard', '');
  };

  const pickSize = (size) => {
    setCodeSize(size);
    if (codeFor) loadCode(codeFor, size, publisher);
  };

  const applyPublisher = () => {
    if (codeFor) loadCode(codeFor, codeSize, publisher);
  };

  const openStats = async (item) => {
    setStatsFor(item);
    setStats(null);
    try {
      const { data } = await client.get(`/admin/ads/${item.id}/sources`);
      setStats(data.items || []);
    } catch {
      setStats([]);
    }
  };

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: `${label} kopyalandı` });
    } catch {
      // Panel HTTPS dışında açıldığında pano API'si yoktur; sessizce
      // geçmek yerine yöneticiye elle seçmesi söylenir.
      toast({
        title: 'Kopyalanamadı',
        description: 'Kod kutusundaki metni elle seçip kopyalayın.',
      });
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ background: 'rgba(224,182,74,0.15)', color: 'var(--v2-warn, #e0b64a)' }}
        >
          <Megaphone size={18} />
        </div>
        <div>
          <h1 className="text-xl font-bold">Reklam Kodları</h1>
          <p className="text-xs text-slate-400">
            Yabancı sitelere verdiğiniz banner kodları — tıklayan ziyaretçi sitenize gelir
          </p>
        </div>
        <button onClick={openNew} className="admin-btn-primary ml-auto flex items-center gap-2">
          <Plus size={14} /> Yeni Reklam
        </button>
      </div>

      <div className="admin-card p-4 mb-4 text-xs text-slate-400 leading-relaxed">
        Her reklam için <b>iki parça kod</b> üretilir: yayıncı sitenin
        <code className="mx-1">&lt;head&gt;</code>bölümüne bir kez konan
        yükleyici ve bannerın çıkacağı yere konan slot. Banner sunucunuzdan
        geldiği için <b>görseli, metni ve hedefi buradan değiştirmeniz yeter</b>
        — karşı siteden kodu güncellemesini istemeniz gerekmez.
      </div>

      {loading ? (
        <div className="admin-card p-10 text-center"><Loader2 className="inline animate-spin text-cyan-400" /></div>
      ) : items.length === 0 ? (
        <div className="admin-card p-10 text-center text-slate-500">
          Henüz reklam eklenmemiş. Bir banner tanımlayın; head ve body kodları
          otomatik oluşur.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((item) => (
            <div key={item.id} className="admin-card p-4">
              <div className="flex items-start gap-3">
                <div className="w-24 h-16 rounded-lg overflow-hidden bg-slate-800 flex items-center justify-center shrink-0">
                  {item.image_url ? (
                    <img
                      src={resolveApiAssetUrl(item.image_url)}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <ImgIcon size={20} className="text-slate-600" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold truncate">{item.name}</div>
                  <div className="text-xs text-slate-400 truncate">
                    {item.headline || 'Metinsiz (görsel banner)'}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    <span className={`badge-pill ${item.is_active ? 'badge-green' : 'badge-gray'}`}>
                      {item.is_active ? 'Yayında' : 'Kapalı'}
                    </span>
                    <span className="badge-pill badge-blue">{sizeLabel(item.size)}</span>
                    <span className="badge-pill badge-gray">{item.slug}</span>
                  </div>
                </div>
              </div>

              <div className="mt-3 flex items-center gap-4 text-xs text-slate-400">
                <span className="flex items-center gap-1">
                  <Eye size={12} /> {(item.impressions || 0).toLocaleString('tr-TR')} gösterim
                </span>
                <span className="flex items-center gap-1">
                  <MousePointerClick size={12} /> {(item.clicks || 0).toLocaleString('tr-TR')} tıklama
                </span>
                <button
                  type="button"
                  onClick={() => openStats(item)}
                  className="ml-auto flex items-center gap-1 text-cyan-400 hover:underline"
                >
                  <BarChart3 size={12} /> Yayıncılar
                </button>
              </div>

              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => openCode(item)}
                  className="admin-btn-primary flex-1 py-2 text-xs flex items-center justify-center gap-1"
                >
                  <Code2 size={12} /> Kodu Al
                </button>
                <button onClick={() => openEdit(item)} className="admin-btn-secondary py-2 px-3 text-xs flex items-center gap-1">
                  <Edit3 size={12} /> Düzenle
                </button>
                <button onClick={() => toggle(item)} className="icon-btn view">
                  {item.is_active ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
                <button onClick={() => remove(item)} className="icon-btn ban"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="absolute inset-0" style={{ background: 'var(--v2-surface, rgba(0,0,0,0.75))', backdropFilter: 'blur(6px)' }} />
          <div className="relative admin-card w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/5 flex items-center gap-2">
              <h3 className="font-bold">{modal.mode === 'new' ? 'Yeni Reklam' : 'Reklamı Düzenle'}</h3>
              <button onClick={() => setModal(null)} className="icon-btn view ml-auto"><X size={14} /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              <F l="Ad (yalnızca panelde görünür)" full>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="admin-input"
                  placeholder="Ör: Hoşgeldin bonusu 728x90"
                />
              </F>
              <F l="Banner Ölçüsü">
                <select
                  value={form.size}
                  onChange={(e) => setForm({ ...form, size: e.target.value })}
                  className="admin-input"
                >
                  {meta.sizes.map((size) => (
                    <option key={size.key} value={size.key}>
                      {size.label} · {size.width}×{size.height}
                    </option>
                  ))}
                </select>
              </F>
              <F l="Tasarım Şablonu">
                <select
                  value={form.template}
                  onChange={(e) => setForm({ ...form, template: e.target.value })}
                  className="admin-input"
                >
                  {meta.templates.map((tpl) => (
                    <option key={tpl.key} value={tpl.key}>{tpl.label}</option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-slate-500">
                  {meta.templates.find((tpl) => tpl.key === form.template)?.note || ''}
                </p>
              </F>
              <F l="Tıklayınca gidilecek sayfa">
                <input
                  value={form.target_url}
                  onChange={(e) => setForm({ ...form, target_url: e.target.value })}
                  className="admin-input"
                  placeholder="Boşsa site ana sayfası"
                />
              </F>
              <F l="Banner Görseli" full>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input
                    value={form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    className="admin-input min-w-0 flex-1"
                    placeholder="Hazır banner görselinizi yükleyin"
                  />
                  <label className={`admin-btn-secondary flex shrink-0 cursor-pointer items-center justify-center gap-2 ${uploading === 'image_url' ? 'pointer-events-none opacity-60' : ''}`}>
                    {uploading === 'image_url' ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    Yükle
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml,.svg"
                      className="sr-only"
                      disabled={uploading === 'image_url'}
                      onChange={(event) => uploadImage(event, 'image_url')}
                    />
                  </label>
                </div>
                <p className="mt-1 text-[11px] text-slate-500">
                  Bu görsel <b>bannerın kendisidir</b>: üstüne yazı basılmaz.
                  Şablon "Otomatik" ya da "Yalnızca görsel" iken kullanılır;
                  diğer şablonlarda bannerı metinler çizer.
                </p>
              </F>
              <F l="Şablon Fon Görseli (isteğe bağlı)" full>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input
                    value={form.background_image_url}
                    onChange={(e) => setForm({ ...form, background_image_url: e.target.value })}
                    className="admin-input min-w-0 flex-1"
                    placeholder="Metinlerin arkasına konacak fon"
                  />
                  <label className={`admin-btn-secondary flex shrink-0 cursor-pointer items-center justify-center gap-2 ${uploading === 'background_image_url' ? 'pointer-events-none opacity-60' : ''}`}>
                    {uploading === 'background_image_url' ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    Yükle
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml,.svg"
                      className="sr-only"
                      disabled={uploading === 'background_image_url'}
                      onChange={(event) => uploadImage(event, 'background_image_url')}
                    />
                  </label>
                </div>
                <p className="mt-1 text-[11px] text-slate-500">
                  Yukarıdakinden farkı: bu <b>arka plandır</b>, üstüne başlık ve
                  düğme çizilir. Yazı okunsun diye otomatik karartılır.
                </p>
              </F>
              {form.image_url && (
                <div className="col-span-2 rounded-lg overflow-hidden bg-slate-800">
                  <img
                    src={resolveApiAssetUrl(form.image_url)}
                    alt=""
                    className="w-full object-contain max-h-[220px]"
                  />
                </div>
              )}
              <F l="Başlık (görsel yoksa)">
                <input
                  value={form.headline}
                  onChange={(e) => setForm({ ...form, headline: e.target.value })}
                  className="admin-input"
                  placeholder="Ör: %100 Hoşgeldin Bonusu"
                />
              </F>
              <F l="Alt Metin (görsel yoksa)">
                <input
                  value={form.subtext}
                  onChange={(e) => setForm({ ...form, subtext: e.target.value })}
                  className="admin-input"
                  placeholder="Ör: Hemen üye ol, bonusunu al"
                />
              </F>
              <F l="Düğme Yazısı">
                <input
                  value={form.cta_text}
                  onChange={(e) => setForm({ ...form, cta_text: e.target.value })}
                  className="admin-input"
                />
              </F>
              <F l="Vurgu Rengi">
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={/^#[0-9a-fA-F]{6}$/.test(form.accent_color) ? form.accent_color : '#e0b64a'}
                    onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                    className="h-[38px] w-[52px] shrink-0 rounded-lg border border-white/10 bg-transparent"
                  />
                  <input
                    value={form.accent_color}
                    onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                    className="admin-input min-w-0 flex-1"
                  />
                </div>
              </F>
              <F l="İkinci Renk (zemin)">
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={/^#[0-9a-fA-F]{6}$/.test(form.accent_color_2) ? form.accent_color_2 : '#1a1030'}
                    onChange={(e) => setForm({ ...form, accent_color_2: e.target.value })}
                    className="h-[38px] w-[52px] shrink-0 rounded-lg border border-white/10 bg-transparent"
                  />
                  <input
                    value={form.accent_color_2}
                    onChange={(e) => setForm({ ...form, accent_color_2: e.target.value })}
                    className="admin-input min-w-0 flex-1"
                    placeholder="Boşsa vurgu renginden türetilir"
                  />
                  {form.accent_color_2 && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, accent_color_2: '' })}
                      className="admin-btn-secondary shrink-0 px-3 text-xs"
                    >
                      Temizle
                    </button>
                  )}
                </div>
              </F>
              <F l="Slot Kimliği (kod içinde geçer)">
                <input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  className="admin-input"
                  placeholder="Boş bırakılırsa addan üretilir"
                />
                <p className="mt-1 text-[11px] text-amber-300/80">
                  Değiştirilirse yayıncı sitelerdeki kod boş kalır.
                </p>
              </F>
              <F l="Sıra">
                <input
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
                  className="admin-input"
                />
              </F>
              <label className="col-span-2 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="accent-cyan-500"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                Yayında (kapatılırsa yayıncı sitelerdeki banner da boşalır)
              </label>
              <button disabled={busy || uploading} onClick={save} className="admin-btn-primary col-span-2 py-3">
                {busy && <Loader2 size={14} className="animate-spin inline mr-2" />}Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {codeFor && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setCodeFor(null)}>
          <div className="absolute inset-0" style={{ background: 'var(--v2-surface, rgba(0,0,0,0.75))', backdropFilter: 'blur(6px)' }} />
          <div className="relative admin-card w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/5 flex items-center gap-2">
              <Code2 size={16} className="text-cyan-400" />
              <h3 className="font-bold">{codeFor.name} — Yayıncı Kodu</h3>
              <button onClick={() => setCodeFor(null)} className="icon-btn view ml-auto"><X size={14} /></button>
            </div>
            <div className="p-5 space-y-4">
              {!codeFor.is_active && (
                <div className="rounded-lg border border-amber-400/25 bg-amber-400/5 px-3 py-2 text-xs text-amber-200">
                  Bu reklam kapalı — kod üretilir ama yayıncı sitede boş görünür.
                  Kartın üzerindeki göz düğmesiyle açın.
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-slate-400 mb-2">Banner ölçüsü</div>
                  <select
                    value={codeSize}
                    onChange={(e) => pickSize(e.target.value)}
                    className="admin-input"
                  >
                    {meta.sizes.map((size) => (
                      <option key={size.key} value={size.key}>
                        {size.label} · {size.width}×{size.height}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="text-xs text-slate-400 mb-2">
                    Yayıncı etiketi (isteğe bağlı)
                  </div>
                  <div className="flex gap-2">
                    <input
                      value={publisher}
                      onChange={(e) => setPublisher(e.target.value)}
                      onBlur={applyPublisher}
                      onKeyDown={(e) => { if (e.key === 'Enter') applyPublisher(); }}
                      className="admin-input min-w-0 flex-1"
                      placeholder="ornek-site.com"
                    />
                    <button type="button" onClick={applyPublisher} className="admin-btn-secondary px-3 text-xs">
                      Uygula
                    </button>
                  </div>
                  <p className="mt-1 text-[11px] text-slate-500">
                    Kod etiketle üretilir; hangi sitenin kaç tıklama getirdiğini ayrı ayrı görürsünüz.
                  </p>
                </div>
              </div>

              <div>
                <div className="text-xs text-slate-400 mb-2">Önizleme</div>
                <div className="rounded-lg bg-slate-900/60 p-4 overflow-auto">
                  {codeLoading || !code ? (
                    <div className="py-8 text-center"><Loader2 className="inline animate-spin text-cyan-400" /></div>
                  ) : (
                    <iframe
                      key={`${codeFor.id}-${codeSize}`}
                      title="Banner önizleme"
                      src={code.preview_url}
                      width={code.width}
                      height={code.height}
                      style={{ border: 0, display: 'block', margin: '0 auto' }}
                      scrolling="no"
                    />
                  )}
                </div>
              </div>

              {code && (
                <>
                  <div className="rounded-lg border border-cyan-400/15 bg-cyan-400/5 px-3 py-2 text-xs text-slate-300">
                    <b>1. adım:</b> aşağıdaki head kodu yayıncı sitenin
                    <code className="mx-1">&lt;/head&gt;</code>etiketinden önce
                    <b> bir kez</b> eklenir. <b>2. adım:</b> body kodu, bannerın
                    çıkmasını istediğiniz yere yapıştırılır. Aynı sayfada birden
                    çok banner varsa head kodu yine tektir.
                  </div>
                  <CodeBox label="1) HEAD kodu — sayfada bir kez" value={code.head} onCopy={copy} />
                  <CodeBox label="2) BODY kodu — bannerın çıkacağı yere" value={code.body} onCopy={copy} />
                  <CodeBox
                    label="Alternatif: tek parça iframe (betiğe izin vermeyen siteler)"
                    value={code.iframe}
                    onCopy={copy}
                  />
                  {code.link && (
                    <CodeBox
                      label="Alternatif: düz görsel bağlantısı (forum/blog)"
                      value={code.link}
                      onCopy={copy}
                    />
                  )}
                  <p className="text-[11px] text-slate-500">
                    Kod {code.site_base || 'site adresiniz'} üzerinden çalışır.
                    Gösterim ve tıklamalar sayılır.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {statsFor && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setStatsFor(null)}>
          <div className="absolute inset-0" style={{ background: 'var(--v2-surface, rgba(0,0,0,0.75))', backdropFilter: 'blur(6px)' }} />
          <div className="relative admin-card w-full max-w-xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/5 flex items-center gap-2">
              <BarChart3 size={16} className="text-cyan-400" />
              <h3 className="font-bold">{statsFor.name} — Yayıncılar</h3>
              <button onClick={() => setStatsFor(null)} className="icon-btn view ml-auto"><X size={14} /></button>
            </div>
            <div className="p-5">
              {stats === null ? (
                <div className="py-8 text-center"><Loader2 className="inline animate-spin text-cyan-400" /></div>
              ) : stats.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6">
                  Henüz veri yok. Kod bir yayıncı sitede çalıştığında burada görünür.
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-slate-400 text-left">
                      <th className="pb-2">Kaynak</th>
                      <th className="pb-2 text-right">Gösterim</th>
                      <th className="pb-2 text-right">Tıklama</th>
                      <th className="pb-2 text-right">Oran</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.map((row) => {
                      const shown = row.impressions || 0;
                      const clicked = row.clicks || 0;
                      const rate = shown ? ((clicked / shown) * 100).toFixed(2) : '—';
                      return (
                        <tr key={row.id} className="border-t border-white/5">
                          <td className="py-2">{row.source}</td>
                          <td className="py-2 text-right">{shown.toLocaleString('tr-TR')}</td>
                          <td className="py-2 text-right">{clicked.toLocaleString('tr-TR')}</td>
                          <td className="py-2 text-right text-slate-400">
                            {rate === '—' ? rate : `%${rate}`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CodeBox = ({ label, value, onCopy }) => (
  <div>
    <div className="flex items-center gap-2 mb-1">
      <span className="text-xs text-slate-400">{label}</span>
      <button
        type="button"
        onClick={() => onCopy(value, 'Kod')}
        className="admin-btn-secondary ml-auto flex items-center gap-1 px-2 py-1 text-[11px]"
      >
        <Copy size={11} /> Kopyala
      </button>
    </div>
    <textarea
      readOnly
      value={value}
      onFocus={(event) => event.target.select()}
      className="admin-input font-mono text-[11px]"
      style={{ minHeight: 62, padding: 10 }}
    />
  </div>
);

const F = ({ l, children, full }) => (
  <div className={full ? 'col-span-2' : ''}>
    <label className="text-xs text-slate-400 block mb-1">{l}</label>
    {children}
  </div>
);

export default AdBanners;
